import type { Column } from '@tanstack/react-table';
import type { ComponentType } from 'react';

import { cn } from '@repo/lib/cn';
import { CheckIcon, PlusIcon, XIcon } from 'lucide-react';
import { startTransition, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button, ButtonGroup } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface DataTableFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: {
    label: string;
    value: string | number | boolean;
    icon?: ComponentType<{ className?: string }>;
  }[];
  onRemove?: () => void;
  onValueChange?: () => void;
}

export default function TanstackTableFacetedFilters<TData, TValue>({ column, title, options, onRemove, onValueChange }: DataTableFacetedFilterProps<TData, TValue>) {
  const [open, setOpen] = useState(false);

  // Use TanStack Table's reactive hooks
  const facets = column?.getFacetedUniqueValues();
  const filterValue = column?.getFilterValue() as (string | number | boolean)[] | undefined;

  const selectedValues = useMemo(() => {
    if (!Array.isArray(filterValue) || filterValue.length === 0) {
      return new Set<string | number | boolean>();
    }
    return new Set(filterValue);
  }, [filterValue]);

  const handleSelect = (optionValue: string | number | boolean) => {
    const newSelectedValues = new Set(selectedValues);
    if (newSelectedValues.has(optionValue)) {
      newSelectedValues.delete(optionValue);
    } else {
      newSelectedValues.add(optionValue);
    }
    const filterValues = Array.from(newSelectedValues);
    const hasValue = filterValues.length > 0;
    const hadValue = selectedValues.size > 0;

    // Mark as non-urgent - filtering is expensive
    startTransition(() => {
      column?.setFilterValue(hasValue ? filterValues : undefined);
    });

    // Notify when filter gets a value for the first time
    if (hasValue && !hadValue) {
      onValueChange?.();
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <ButtonGroup>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className={cn(selectedValues.size === 0 && 'border-dashed')}>
            {!selectedValues.size && <PlusIcon />}
            <span className="truncate">{title}</span>
            <Badge variant={'muted'} size={'sm'} border={false} className={cn(selectedValues.size === 0 && 'hidden', 'px-1 py-0.5 text-xs h-5')}>
              +{selectedValues.size}
            </Badge>
          </Button>
        </DropdownMenuTrigger>
        <Button
          variant="outline"
          size="sm"
          className={cn(selectedValues.size === 0 && 'border-dashed')}
          onClick={() => {
            startTransition(() => {
              column?.setFilterValue(undefined);
            });
            setOpen(false);
            onRemove?.();
          }}
        >
          <XIcon />
        </Button>
      </ButtonGroup>
      <DropdownMenuContent className="w-48" align="start">
        <DropdownMenuLabel>{title}</DropdownMenuLabel>
        <DropdownMenuGroup>
          {options.map((option) => {
            const isSelected = selectedValues.has(option.value);
            return (
              <DropdownMenuItem
                key={option.value.toString()}
                onSelect={(e) => {
                  e.preventDefault();
                  handleSelect(option.value);
                }}
              >
                <div className={cn('mr-2 flex h-4 w-4 items-center justify-center', isSelected ? 'text-foreground' : 'opacity-60 [&_svg]:invisible')}>
                  <CheckIcon className={'size-4'} />
                </div>
                {option.icon && <option.icon className="text-foreground mr-2 size-4" />}
                <span>{option.label}</span>
                {facets?.get(option.value) && <span className="ml-auto flex size-4 items-center justify-center text-xs">{facets.get(option.value)}</span>}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>
        {selectedValues.size > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  startTransition(() => {
                    column?.setFilterValue(undefined);
                  });
                  setOpen(false);
                  onRemove?.();
                }}
                className="justify-center text-center"
              >
                Wyczyść
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
